import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import TextAreaInput from '../../components/TextAreaInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import seoSettingApi from '../../api/seoSettingApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const EditSEOSetting = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // 'id' will be 'create' for new, or actual ID for edit
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    page: '',
    meta_title: '',
    meta_description: '',
    meta_keyword: '',
    canonical_link: '',
  });
  const [isEditMode, setIsEditMode] = useState(false); // To distinguish between create and edit

  useEffect(() => {
    const fetchSetting = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});

      if (id === 'create') {
        setIsEditMode(false);
        setFormData({
          page: '',
          meta_title: '',
          meta_description: '',
          meta_keyword: '',
          canonical_link: '',
        });
        setLoading(false);
      } else {
        setIsEditMode(true);
        try {
          const response = await seoSettingApi.getSeoSettingByIdentifier(id);
          if (response.success && response.data) {
            const fetchedSetting = response.data;
            setFormData({
              page: fetchedSetting.page,
              meta_title: fetchedSetting.meta_title,
              meta_description: fetchedSetting.meta_description,
              meta_keyword: fetchedSetting.meta_keyword,
              canonical_link: fetchedSetting.canonical_link,
            });
          } else {
            setGeneralError(response.message || 'SEO setting not found.');
          }
        } catch (err) {
          console.error('Failed to fetch SEO setting:', err);
          setGeneralError(err.response?.data?.message || 'Failed to load SEO setting. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchSetting();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      data.append(key, formData[key]);
    }

    try {
      if (isEditMode) {
        await seoSettingApi.updateSeoSetting(id, data);
        addToast('SEO setting updated successfully!', 'success');
      } else {
        await seoSettingApi.createSeoSetting(data);
        addToast('SEO setting created successfully!', 'success');
      }
      navigate(ROUTES.SEO_SETTINGS);
    } catch (err) {
      console.error('Failed to save SEO setting:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to save SEO setting. Please try again.', 'error');
        setGeneralError(err.response?.data?.message || 'Failed to save SEO setting. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (generalError && isEditMode) { // Only show error if in edit mode and record not found
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">SEO Setting Not Found</h1>
        <p className="text-text-gray">{generalError}</p>
        <Button
          onClick={() => navigate(ROUTES.SEO_SETTINGS)}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to SEO Settings List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">{isEditMode ? `Edit SEO Setting for Page: ${formData.page}` : 'Create New SEO Setting'}</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Page"
          name="page"
          id="page"
          value={formData.page}
          onChange={handleChange}
          required={true}
          placeholder="Page Name (e.g., home, about-us)"
          error={validationErrors?.page?.[0]}
          disabled={isEditMode} // Page name is editable only during creation
          className={isEditMode ? 'cursor-not-allowed' : ''}
        />
        <TextInput
          label="Meta Title"
          name="meta_title"
          id="meta_title"
          value={formData.meta_title}
          onChange={handleChange}
          required={true}
          placeholder="Meta Title"
          error={validationErrors?.meta_title?.[0]}
        />
        <TextAreaInput
          label="Meta Description"
          name="meta_description"
          id="meta_description"
          value={formData.meta_description}
          onChange={handleChange}
          required={true}
          rows={4}
          placeholder="Meta Description"
          error={validationErrors?.meta_description?.[0]}
        />
        <TextInput
          label="Meta Keywords (comma separated)"
          name="meta_keyword"
          id="meta_keyword"
          value={formData.meta_keyword}
          onChange={handleChange}
          placeholder="Meta Keywords (comma separated)"
          error={validationErrors?.meta_keyword?.[0]}
        />
        <TextInput
          label="Canonical Link"
          name="canonical_link"
          id="canonical_link"
          value={formData.canonical_link}
          onChange={handleChange}
          placeholder="https://example.com/page"
          error={validationErrors?.canonical_link?.[0]}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            {isEditMode ? 'Update SEO Setting' : 'Create SEO Setting'}
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.SEO_SETTINGS)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditSEOSetting;